import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import BannerImage from '../assets/Space.jpg';
import '../submission/submission.css';
import '../card/card.css';
import './users.css';

const emptyCreateForm = {
  username: '',
  password: '',
  role: 'user',
  is_enabled: true,
};

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [savingSetting, setSavingSetting] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [pageMessage, setPageMessage] = useState('');
  const [pageError, setPageError] = useState('');

  const loadUsers = async () => {
    setLoading(true);
    setPageError('');

    try {
      const response = await axios.get('https://api.alientrader.cards/admin/users');
      const nextUsers = response.data.users || [];

      setUsers(nextUsers);
      setRegistrationEnabled(Boolean(response.data.public_registration_enabled));
      setDrafts(
        nextUsers.reduce((acc, user) => {
          acc[user.username] = {
            role: user.role,
            is_enabled: user.is_enabled,
            password: '',
          };
          return acc;
        }, {})
      );
    } catch (error) {
      console.error('Error loading users:', error);
      setPageError(error?.response?.data?.message || 'Unable to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleDraftChange = (username, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [username]: {
        ...prev[username],
        [field]: value,
      },
    }));
  };

  const handleSaveUser = async (username) => {
    const draft = drafts[username];
    if (!draft) {
      return;
    }

    setPageError('');
    setPageMessage('');

    try {
      const payload = {
        role: draft.role,
        is_enabled: Boolean(draft.is_enabled),
      };

      if (draft.password.trim()) {
        payload.password = draft.password;
      }

      const response = await axios.put(`https://api.alientrader.cards/admin/users/${encodeURIComponent(username)}`, payload);
      const updatedUser = response.data.user;

      setUsers((prev) =>
        prev.map((user) => (user.username === username ? updatedUser : user))
      );
      setDrafts((prev) => ({
        ...prev,
        [username]: {
          role: updatedUser.role,
          is_enabled: updatedUser.is_enabled,
          password: '',
        },
      }));
      setPageMessage(`Saved changes for ${username}.`);
    } catch (error) {
      console.error('Error saving user:', error);
      setPageError(error?.response?.data?.message || `Unable to save ${username}.`);
    }
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setCreatingUser(true);
    setPageError('');
    setPageMessage('');

    try {
      const response = await axios.post('https://api.alientrader.cards/admin/users', createForm);
      const createdUser = response.data.user;

      setUsers((prev) => [...prev, createdUser].sort((a, b) => a.username.localeCompare(b.username)));
      setDrafts((prev) => ({
        ...prev,
        [createdUser.username]: {
          role: createdUser.role,
          is_enabled: createdUser.is_enabled,
          password: '',
        },
      }));
      setCreateForm(emptyCreateForm);
      setPageMessage(`Created ${createdUser.username}.`);
    } catch (error) {
      console.error('Error creating user:', error);
      setPageError(error?.response?.data?.message || 'Unable to create user.');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleToggleRegistration = async () => {
    setSavingSetting(true);
    setPageError('');
    setPageMessage('');

    try {
      const nextValue = !registrationEnabled;
      const response = await axios.put('https://api.alientrader.cards/admin/settings/public-registration', {
        enabled: nextValue,
      });
      setRegistrationEnabled(Boolean(response.data.enabled));
      setPageMessage(`Public registration ${response.data.enabled ? 'enabled' : 'disabled'}.`);
    } catch (error) {
      console.error('Error updating registration setting:', error);
      setPageError(error?.response?.data?.message || 'Unable to update registration setting.');
    } finally {
      setSavingSetting(false);
    }
  };

  return (
    <div className="imagesPage admin-users-wall">
      <div
        className="subTopBG"
        style={{
          backgroundImage: `url(${BannerImage})`,
          backgroundPosition: 'center -40px',
        }}
      >
        <div className="actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="subBack"></div>
          <div className="sub" style={{ color: '#f4f7fb' }}></div>
          <div className="subEdit" style={{ width: 'auto', marginRight: '0' }}></div>
        </div>
        <div className="subHeader">User Admin</div>
      </div>

      <div className="subPositionAdjust" style={{ top: '-130px' }}>
        <Container fluid style={{ paddingLeft: '30px', paddingRight: '30px' }}>
          <Row className="d-flex justify-content-center" style={{ marginTop: '28px' }}>
            <Col xxl={10} xl={11} lg={12} md={12} className="custom-info-box shadow-sm admin-users-shell">
              <Row>
                <div className="custom-info-header">User Management</div>
              </Row>
              <Row>
                <div className="custom-info-misc admin-users-summary-text">
                  <button type="button" className="admin-users-summary-chip active">
                    {`${users.length} Users`}
                  </button>
                  <button type="button" className="admin-users-summary-chip admin-users-summary-chip-admin">
                    {`${users.filter((user) => user.role === 'admin').length} Admins`}
                  </button>
                  <button type="button" className="admin-users-summary-chip admin-users-summary-chip-user">
                    {`${users.filter((user) => user.role === 'user').length} Users`}
                  </button>
                  <button
                    type="button"
                    className={`admin-users-summary-chip ${registrationEnabled ? 'admin-users-summary-chip-enabled' : 'admin-users-summary-chip-disabled'}`}
                  >
                    {registrationEnabled ? 'Registration Enabled' : 'Registration Disabled'}
                  </button>
                </div>
              </Row>
              <Row>
                <Col lg={6} md={12} className="imagesPageRow d-flex align-items-stretch justify-content-center">
                  <div className="admin-users-card shadow-sm">
                    <div className="admin-users-card-header">
                      <div>
                        <div className="admin-users-card-title">Registration</div>
                        <div className="admin-users-card-subtitle">Control whether new users can self-register.</div>
                      </div>
                      <span className={`admin-users-state-pill ${registrationEnabled ? 'is-on' : 'is-off'}`}>
                        {registrationEnabled ? 'Open' : 'Closed'}
                      </span>
                    </div>
                    <div className="admin-users-card-body">
                      <div className="admin-users-status-copy">
                        {registrationEnabled
                          ? 'Anyone with the registration link can create an account.'
                          : 'Public signup is blocked until you enable it again.'}
                      </div>
                      <Button
                        variant={registrationEnabled ? 'danger' : 'success'}
                        onClick={handleToggleRegistration}
                        disabled={savingSetting}
                      >
                        {registrationEnabled ? 'Disable Registration' : 'Enable Registration'}
                      </Button>
                    </div>
                  </div>
                </Col>
                <Col lg={6} md={12} className="imagesPageRow d-flex align-items-stretch justify-content-center">
                  <div className="admin-users-card shadow-sm">
                    <div className="admin-users-card-header">
                      <div>
                        <div className="admin-users-card-title">Create User</div>
                        <div className="admin-users-card-subtitle">Add a user account directly from the dashboard.</div>
                      </div>
                    </div>
                    <div className="admin-users-card-body">
                      <Form onSubmit={handleCreateUser}>
                        <Row className="g-2">
                          <Col md={6}>
                            <Form.Label>Username</Form.Label>
                            <Form.Control
                              value={createForm.username}
                              onChange={(event) =>
                                setCreateForm((prev) => ({ ...prev, username: event.target.value }))
                              }
                              required
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                              type="password"
                              value={createForm.password}
                              onChange={(event) =>
                                setCreateForm((prev) => ({ ...prev, password: event.target.value }))
                              }
                              required
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Label>Role</Form.Label>
                            <Form.Select
                              value={createForm.role}
                              onChange={(event) =>
                                setCreateForm((prev) => ({ ...prev, role: event.target.value }))
                              }
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </Form.Select>
                          </Col>
                          <Col md={6} className="admin-users-checkbox-col">
                            <Form.Check
                              type="switch"
                              id="create-user-enabled"
                              label="Enabled"
                              checked={createForm.is_enabled}
                              onChange={(event) =>
                                setCreateForm((prev) => ({ ...prev, is_enabled: event.target.checked }))
                              }
                            />
                          </Col>
                        </Row>
                        <div className="admin-users-actions">
                          <Button type="submit" disabled={creatingUser}>
                            {creatingUser ? 'Creating...' : 'Create User'}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  {(pageMessage || pageError) && (
                    <div className="custom-info-misc admin-users-feedback">
                      {pageMessage && <span className="admin-users-message">{pageMessage}</span>}
                      {pageError && <span className="admin-users-error">{pageError}</span>}
                    </div>
                  )}
                </Col>
              </Row>
              <Row className="subCardWall d-flex align-items-center justify-content-center">
                {loading ? (
                  <Col>
                    <div className="custom-info-misc admin-users-empty">Loading users...</div>
                  </Col>
                ) : users.length > 0 ? (
                  users.map((user) => {
                    const draft = drafts[user.username] || {
                      role: user.role,
                      is_enabled: user.is_enabled,
                      password: '',
                    };

                    return (
                      <Col xxl={4} xl={6} lg={6} md={12} className="imagesPageRow d-flex align-items-stretch justify-content-center" key={user.username}>
                        <div className="admin-users-user-card shadow-sm">
                          <div className="admin-users-user-header">
                            <div className="admin-users-user-heading">
                              <span className="admin-users-user-title">{user.username}</span>
                              <span className="admin-users-user-subtitle">
                                Current role: {user.role}
                              </span>
                            </div>
                            <div className={`admin-users-user-pill ${draft.is_enabled ? 'is-enabled' : 'is-disabled'}`}>
                              {draft.is_enabled ? 'Enabled' : 'Disabled'}
                            </div>
                          </div>
                          <div className="admin-users-user-form">
                            <div className="admin-users-field">
                              <Form.Label>Role</Form.Label>
                              <Form.Select
                                value={draft.role}
                                onChange={(event) => handleDraftChange(user.username, 'role', event.target.value)}
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                              </Form.Select>
                            </div>
                            <div className="admin-users-field">
                              <Form.Label>Reset Password</Form.Label>
                              <Form.Control
                                type="password"
                                placeholder="Leave blank to keep current"
                                value={draft.password}
                                onChange={(event) => handleDraftChange(user.username, 'password', event.target.value)}
                              />
                            </div>
                            <div className="admin-users-user-footer">
                              <Form.Check
                                type="switch"
                                id={`enabled-${user.username}`}
                                label="Enabled"
                                checked={Boolean(draft.is_enabled)}
                                onChange={(event) => handleDraftChange(user.username, 'is_enabled', event.target.checked)}
                              />
                              <Button onClick={() => handleSaveUser(user.username)}>
                                Save
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Col>
                    );
                  })
                ) : (
                  <Col>
                    <div className="custom-info-misc admin-users-empty">No users found.</div>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export default AdminUsers;
